import { useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { VARIANT_SPECS, toGameVariant, Color, PieceType, type BoardNotation } from '@draughts/shared';
import { squareToSVGCoords, isPlayableSquare } from '../../utils/board-coordinates';
import { usePreferencesStore } from '../../store/preferences-store';
import { variantPieceColors } from '../game/piece-themes';
import { usePieceColorPair, useBoardColors } from '../game/use-appearance';
import { PieceLodContext, useBoardLod } from '../game/piece-lod';
import Piece from '../game/Piece';
import BoardSlab from '../game/BoardSlab';
import BoardCoords from '../game/BoardCoords';
import { glassIncidence } from '../game/board-materials';

/**
 * Shared puzzle preview board.
 *
 * Renders an SVG draughts board with pieces decoded from a FEN string.
 * Used by both the legacy home/DailyPuzzleCard and the new
 * dashboard/DashboardDailyPuzzleCard so the FEN parser + SVG drawing
 * code is not duplicated.
 */

export interface PuzzlePiece {
  square: number;
  color: 'white' | 'black';
  type: 'man' | 'king';
}

/** Parse a Polish-notation draughts FEN into a piece list.
 *  Supports explicit lists ("W:W21,22,K31:B1,2,3") and hyphen ranges as used by
 *  the variant initial positions ("W:W21-32:B1-12", "W:W41-56:B9-24"). A range
 *  may carry a leading "K" (e.g. "K21-32") to mark every square as a king. */
export function parseFen(fen: string): PuzzlePiece[] {
  const pieces: PuzzlePiece[] = [];
  for (const seg of fen.split(':')) {
    if (!seg) continue;
    const colorChar = seg[0];
    if (colorChar !== 'W' && colorChar !== 'B') continue;
    const list = seg.slice(1);
    if (!list) continue;
    const color = colorChar === 'W' ? 'white' : 'black';
    for (const tok of list.split(',')) {
      const trimmed = tok.trim();
      if (!trimmed) continue;
      const isKing = trimmed.startsWith('K');
      const numStr = isKing ? trimmed.slice(1) : trimmed;
      const type = isKing ? 'king' : 'man';
      const dash = numStr.indexOf('-');
      if (dash > 0) {
        // Range "start-end" → one piece per square (inclusive, either direction).
        const start = parseInt(numStr.slice(0, dash), 10);
        const end = parseInt(numStr.slice(dash + 1), 10);
        if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
        const lo = Math.min(start, end);
        const hi = Math.max(start, end);
        for (let square = lo; square <= hi; square++) {
          pieces.push({ square, color, type });
        }
      } else {
        const square = parseInt(numStr, 10);
        if (!Number.isFinite(square)) continue;
        pieces.push({ square, color, type });
      }
    }
  }
  return pieces;
}

interface Props {
  fen: string;
  variant: string;
  flipped?: boolean;
  squareSize?: number;
  /** CSS class on the root <svg>. Lets each consumer skin the board
   *  independently while sharing the layout code. */
  className?: string;
  /** CSS class for dark / light squares. Defaults to the dashboard's
   *  `dp-sq-dark` / `dp-sq-light`. */
  darkSquareClass?: string;
  lightSquareClass?: string;
  ariaLabel?: string;
  /** Caselle da evidenziare (es. il percorso di una presa nelle pagine regole).
   *  Puramente decorativo: disegna un anello sotto ai pezzi, non cambia la FEN. */
  markSquares?: number[];
  /** Opt-in: usa i colori scelti dall'utente (tema scacchiera + tema pezzi) invece
   *  dei colori fissi per-variante. I PEZZI sono sempre il componente <Piece> reale,
   *  quindi forma, stile 3D e marcatura della dama seguono comunque le preferenze;
   *  `themed` governa solo la PALETTE. Resta spento dove i colori per-variante sono
   *  voluti (pagine regole, landing). */
  themed?: boolean;
  /** Etichette delle caselle sul diagramma. Default 'none': l'anteprima resta nuda per
   *  tutti i chiamanti che non la chiedono. Le pagine regole la forzano a 'numeric'
   *  perché le loro didascalie citano le mosse per numero. */
  notation?: BoardNotation;
}

export default function PuzzlePreviewBoard({
  fen,
  variant,
  flipped = false,
  squareSize = 32,
  className = 'dash-puzzle-board-svg',
  darkSquareClass = 'dp-sq-dark',
  lightSquareClass = 'dp-sq-light',
  ariaLabel = 'Puzzle board preview',
  markSquares,
  themed = false,
  notation = 'none',
}: Props) {
  // Derive geometry from the canonical variant spec so every variant (8/10/12/14
  // boards, all-squares vs dark-squares, board orientation) renders correctly.
  // Falls back to the legacy heuristic if the variant string is unknown.
  const gv = toGameVariant(variant);
  const spec = VARIANT_SPECS[gv];

  // Theme-aware rendering (opt-in). Mirrors GameBoard / PreferencesPreview:
  // resolve the user's board + piece theme against this puzzle's variant so the
  // preview matches the real board (and the 'auto' theme shows the per-variant
  // default). Hooks are called unconditionally; the result is only used when
  // `themed` is set.
  const boardMaterial = usePreferencesStore((s) => s.boardMaterial);
  const boardColors = useBoardColors(gv);
  // Senza `themed` i colori restano quelli della variante (regole, landing), ma il
  // pezzo è comunque il <Piece> reale: cambia la palette, non il renderer.
  const themedPieceColors = usePieceColorPair(gv);
  const variantColors = useMemo(() => variantPieceColors(gv), [gv]);
  const pieceColorPair = themed ? themedPieceColors : variantColors;
  const boardSize = spec ? spec.board.size : variant === 'international' ? 10 : 8;
  // `italian` here means "dark square in the bottom-right" orientation.
  const italian = spec ? spec.ui.darkSquareBottomRight : variant === 'italian';
  // All-squares variants use row-major numbering across the whole board.
  const allSquares = spec
    ? spec.board.playableSquares === 'all'
    : variant === 'turkish' || variant === 'armenian' || variant === 'dameo' || variant === 'croda';
  const total = boardSize * squareSize;
  const svgRef = useRef<SVGSVGElement>(null);
  // squareSize * 0.76 = radius(0.38) * 2 — la dimensione storica del pezzo qui.
  const pieceLod = useBoardLod(svgRef, total, squareSize * 0.76);
  const pieces = useMemo(() => parseFen(fen), [fen]);
  const squares = useMemo(() => {
    const out: { row: number; col: number; dark: boolean }[] = [];
    for (let r = 1; r <= boardSize; r++) {
      for (let c = 1; c <= boardSize; c++) {
        out.push({ row: r, col: c, dark: isPlayableSquare(r, c, boardSize, italian) });
      }
    }
    return out;
  }, [boardSize, italian]);

  return (
    <PieceLodContext.Provider value={pieceLod}>
    <svg
      ref={svgRef}
      className={className}
      viewBox={`0 0 ${total} ${total}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      style={
        themed
          ? ({
              '--color-square-light': boardColors.light,
              '--color-square-dark': boardColors.dark,
            } as CSSProperties)
          : undefined
      }
    >
      {squares.map((s) => {
        const dr = flipped ? boardSize + 1 - s.row : s.row;
        const dc = flipped ? boardSize + 1 - s.col : s.col;
        return (
          <rect
            key={`${s.row}-${s.col}`}
            x={(dc - 1) * squareSize}
            y={(dr - 1) * squareSize}
            width={squareSize}
            height={squareSize}
            className={s.dark ? darkSquareClass : lightSquareClass}
            style={
              themed
                ? { fill: s.dark ? 'var(--color-square-dark)' : 'var(--color-square-light)' }
                : undefined
            }
          />
        );
      })}

      {/* Solo con `themed`: senza, l'anteprima resta ancorata ai colori della variante
          (regole, landing) e non deve seguire nemmeno il materiale. */}
      {themed && <BoardSlab size={total} squareSize={squareSize} darkSquareBottomRight={italian} material={boardMaterial} />}
      {/* Percorso evidenziato: sotto ai pezzi, così non ne copre mai la forma. */}
      {markSquares?.map((square) => {
        let { x, y } = squareToSVGCoords(square, squareSize, boardSize, italian, allSquares);
        if (flipped) {
          x = total - x;
          y = total - y;
        }
        return (
          <circle
            key={`m-${square}`}
            className="pp-mark"
            cx={x}
            cy={y}
            r={squareSize * 0.44}
            fill="none"
            strokeWidth={squareSize * 0.09}
          />
        );
      })}
      {/* Etichette delle caselle: dopo gli anelli, che le passano sotto, e prima dei
          pezzi, che le coprono — lo stesso ordine della scacchiera di gioco. `mirror` (il
          default) è il modo giusto qui: questa scacchiera specchia le coordinate in JS e
          non ruota l'SVG. */}
      <BoardCoords
        notation={notation}
        boardSize={boardSize}
        squareSize={squareSize}
        darkSquareBottomRight={italian}
        allSquares={allSquares}
        flipped={flipped}
      />
      {pieces.map((p) => {
        let { x, y } = squareToSVGCoords(p.square, squareSize, boardSize, italian, allSquares);
        if (flipped) {
          x = total - x;
          y = total - y;
        }
        const radius = squareSize * 0.38;
        return (
          <Piece
            key={`p-${p.square}`}
            piece={{
              color: p.color === 'white' ? Color.WHITE : Color.BLACK,
              type: p.type === 'king' ? PieceType.KING : PieceType.MAN,
              square: p.square,
            }}
            x={x}
            y={y}
            size={radius * 2}
            colorPair={pieceColorPair}
            // Stesso gate `themed` della lastra: senza tema, nessun materiale.
            reflection={themed && boardMaterial === 'glass'}
            reflectionIncidence={glassIncidence(x, y, total, squareSize)}
            // `flipped` NON si inoltra: qui il ribaltamento e' gia' nelle coordinate (come
            // in EditorBoard), l'SVG non ruota, e passarlo capovolgerebbe ogni pezzo — il
            // fianco scuro del cilindro sopra la faccia e l'emblema della dama a testa in
            // giu'. Vedi la nota sul prop in Piece.tsx.
          />
        );
      })}
    </svg>
    </PieceLodContext.Provider>
  );
}
